import { useDispatch, useSelector } from 'react-redux';
import React, { forwardRef, useMemo, useState } from 'react';
import {
  getFullVendorQuotes,
  getLowestPrice,
} from '../../../../../components/MultipleVendorsAcceptance/vendorQuoteUtil.jsx';
import { LeasingAreqVendorAcceptance } from './vendor-quote-acceptance.jsx';
import { generateVendorAcceptanceForm } from './vendor-acceptance-form.jsx';
import { FormGenerator } from '@gsa/afp-shared-form-utils';
import { vendorAcceptanceSchema } from './vendor-acceptance-schema.jsx';
import {
  useGetAreqVendorQuotes,
  useGetContractLineModelColors,
} from '../../../../../requests/leasing.jsx';
import { setAreqVendorQuotes, setSelectedVendorMakeColors } from '../../../../../reducers/leasing.jsx';
import { LeasingAREQMultipleVendorInstructions } from './multiple-vendor-instructions.jsx';

export default forwardRef(function OrderingAdminApprovalActions({}, ref) {
  const dispatch = useDispatch();
  const selectedVendorMakeColors = useSelector((state) => state.leasingReducer.selectedVendorMakeColors);
  const requisitionId = useSelector(
    (state) => state.leasingReducer.requisitionId,
  );
  const [getAreqVendorQuotes, { loading: vendorQuotesLoading }] =
    useGetAreqVendorQuotes();

  const compareSelect = useSelector(
    (state) => state.leasingReducer.compareSelect,
  );
  const vendorQuotes = useSelector(
    (state) => state.leasingReducer.areqVendorQuotes,
  );
  const requisitionStatus = useSelector(
    (state) => state.leasingReducer.requisitionStatus,
  );
  const selectedVendorQuote = useSelector(
    (state) => state.leasingReducer.selectedVendorQuote,
  );

  const { calculatedPriceBreakdown, activeContracts } = compareSelect;

  useMemo(async () => {
    if (requisitionId) {
      const vendorQuotesRes = await getAreqVendorQuotes({
        variables: {
          requisitionId: requisitionId,
        },
      });
      if (vendorQuotesRes?.data) {
        dispatch(setAreqVendorQuotes(vendorQuotesRes?.data));
      }
    }
  }, [requisitionId]);
  let fullVendorQuotes = useMemo(
    () =>
      activeContracts?.length && calculatedPriceBreakdown?.length
        ? getFullVendorQuotes(
            vendorQuotes,
            activeContracts,
            calculatedPriceBreakdown,
          )
        : vendorQuotes,
    [vendorQuotes, activeContracts, calculatedPriceBreakdown],
  );
  const lowestPrice = useMemo(() => {
    if (fullVendorQuotes && fullVendorQuotes.length) {
      return getLowestPrice(fullVendorQuotes);
    }
  }, [fullVendorQuotes]);
  const [getContractLineColors] = useGetContractLineModelColors();
  const nonLowBidVendorSelected = useMemo(() => {
    if (selectedVendorQuote && selectedVendorQuote.totalPrice) {
      return selectedVendorQuote.totalPrice > lowestPrice;
    }
  }, [selectedVendorQuote, lowestPrice]);
  let formContent = null;

  useMemo(async () => {
    if (selectedVendorQuote) {
      let tempAvailableColors = [];
      const res = await getContractLineColors({
        variables: {
          contractLineId: selectedVendorQuote?.contractLineId,
        },
      });
      if (res?.data?.length > 0) {
        tempAvailableColors = res?.data?.map((x) => {
          return {
            label: x.description,
            value: x.colorCode,
            price: x.price,
            vendorPrice: x.vendorPrice,
          };
        });
      }
      dispatch(setSelectedVendorMakeColors(tempAvailableColors));
    }
  }, [selectedVendorQuote, nonLowBidVendorSelected]);
  formContent = useMemo(
    () =>
      generateVendorAcceptanceForm({
        colors: selectedVendorMakeColors,
        nonLowBidSelected: nonLowBidVendorSelected,
      }),
    [selectedVendorMakeColors, nonLowBidVendorSelected],
  );
  return (
    <>
      <LeasingAREQMultipleVendorInstructions />
      {!selectedVendorQuote ? (
        fullVendorQuotes &&
        fullVendorQuotes.map((vendorQuote, index) => {
          return vendorQuote.errorMsg ? (
            <div className="error-msg">{vendorQuote.errorMsg}</div>
          ) : (
            <>
              <div role="tab" tabIndex="0" className="vendor-quotes">
                <LeasingAreqVendorAcceptance
                  vendorQuote={vendorQuote}
                  status={requisitionStatus}
                  lowestPrice={lowestPrice}
                  index={index}
                />
              </div>
            </>
          );
        })
      ) : (
        <>
          <div role="tab" tabIndex="0" className="vendor-quotes">
            <LeasingAreqVendorAcceptance
              vendorQuote={selectedVendorQuote}
              status={requisitionStatus}
              lowestPrice={lowestPrice}
              index={0}
            />
          </div>
          {formContent && (
            <FormGenerator
              ref={ref}
              content={formContent}
              id="vendor-acceptance-form"
              className="vendor-acceptance-form"
              schema={vendorAcceptanceSchema({nonLowBidVendorSelected: nonLowBidVendorSelected})}
              defaultValues={{ modelColor: '' }}
              useFormProps={{
                mode: 'all',
              }}
            />
          )}
        </>
      )}
    </>
  );
});
